import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { StyledButton } from '@app/components/button/Button';
import { SelectField } from '@app/components/form-fields/SelectField';
import { TextAreaField } from '@app/components/form-fields/TextAreaField';
import { TextField } from '@app/components/form-fields/TextField';
import { UploadField } from '@app/components/form-fields/UploadField';
import { textAgentModels } from '@app/utils/constants';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import type { UseFormReset } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { MdCheck } from 'react-icons/md';
import * as z from 'zod';

const schema = z.object({
    name: z.string().trim().min(1, 'Name is required'),
    model_name: z.enum(textAgentModels, 'Model is required'),
    system_prompt: z.string().trim().min(1, 'Prompt is required'),
    logo: z.union([z.instanceof(File), z.string()]).optional(),
});

export type TextAgentFormValues = z.infer<typeof schema>;

export type TextAgentFormProps = {
    id: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any;
    loading?: boolean;
    onCancel?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    onSubmit: (data: TextAgentFormValues, reset: UseFormReset<TextAgentFormValues>) => void;
};

export const TextAgentForm = ({ onSubmit, onCancel, data, ...props }: TextAgentFormProps) => {
    const {
        handleSubmit,
        control,
        reset,
        formState: { isValid, isSubmitting },
    } = useForm<TextAgentFormValues>({
        mode: 'all',
        resolver: zodResolver(schema),
        defaultValues: {
            name: '',
            model_name: undefined,
            system_prompt: '',
            logo: '',
        },
    });

    const modelOptions = textAgentModels.map((model) => ({
        label: model,
        value: model,
    }));

    useEffect(() => {
        reset(data);
    }, [data, reset]);

    const handleFormSubmit = ({ logo, ...rest }: TextAgentFormValues) => {
        onSubmit(logo instanceof File ? { ...rest, logo } : rest, reset);
    };

    return (
        <form onSubmit={handleSubmit(handleFormSubmit)} id={props.id}>
            <div className="form-fields-wrapper">
                <div className="grid">
                    <div className="grid-xs-12">
                        <Controller
                            control={control}
                            name="name"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Name"
                                    type="text"
                                    placeholder="Enter name"
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="model_name"
                            render={({ field, fieldState }) => (
                                <SelectField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Model"
                                    placeholder="Select model"
                                    options={modelOptions}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="system_prompt"
                            render={({ field, fieldState }) => (
                                <TextAreaField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Prompt"
                                    placeholder="Enter prompt"
                                    rows={8}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="logo"
                            render={({ field, fieldState }) => (
                                <UploadField fieldState={fieldState} {...field} label="Logo" />
                            )}
                        />
                    </div>
                </div>
            </div>

            <div className="ant-modal-footer">
                <StyledButtonGroup justify="flex-end">
                    <StyledButton onClick={onCancel} color="black" data-testid="reset-button">
                        Cancel
                    </StyledButton>
                    <StyledButton
                        htmlType="submit"
                        type="primary"
                        disabled={!isValid}
                        loading={isSubmitting || props?.loading}
                        icon={<MdCheck />}
                        data-testid="add-text-agent-button">
                        Save
                    </StyledButton>
                </StyledButtonGroup>
            </div>
        </form>
    );
};
